import { motion } from "framer-motion";
import { Link } from "react-router-dom";
import { ArrowRight, Building2, Store, PackageCheck } from "lucide-react";
import { useContent } from "@/context/ContentContext";

const FALLBACK = {
  chip: "Retail · Wholesale · Distribution",
  title_line1: "Quality You Can Count On.",
  title_line2: "Ready for Every Home.",
  subtitle: "From our Texas facility to shelves and homes nationwide, Morning Soft keeps retailers stocked and families supplied with dependable everyday essentials.",
  image_truck: "/products/img_02.png",
  image_factory: "/products/img_05.png",
  image_shelf: "/products/img_01.png",
  factory_title: "Made in Texas.",
  factory_copy: "Efficient U.S.-based production keeps quality high and lead times short.",
  shelf_title: "Ready for retail.",
  shelf_copy: "Attractive packaging built to be seen, and picked up, again and again.",
};

export default function TruckBanner() {
  const { content } = useContent();
  const t = { ...FALLBACK, ...(content?.truck || {}) };
  return (
    <section className="relative overflow-hidden bg-white" data-testid="truck-banner">
      <div className="mx-auto max-w-7xl px-5 sm:px-8 py-20 md:py-28">
        <motion.div
          initial={{ opacity: 0, y: 30, scale: 0.98 }}
          whileInView={{ opacity: 1, y: 0, scale: 1 }}
          viewport={{ once: true, margin: "-80px" }}
          transition={{ duration: 0.9, ease: [0.22, 1, 0.36, 1] }}
          className="relative rounded-[36px] overflow-hidden shadow-[0_40px_80px_-30px_rgba(23,58,122,0.5)] border border-white/60"
          data-testid="truck-hero-frame"
        >
          <motion.img
            src={t.image_truck}
            alt="Morning Soft"
            className="w-full h-[420px] md:h-[560px] object-cover"
            initial={{ scale: 1.08 }}
            whileInView={{ scale: 1 }}
            viewport={{ once: true }}
            transition={{ duration: 2.5, ease: [0.22, 1, 0.36, 1] }}
            animate={{ y: [0, -8, 0] }}
          />
          <div aria-hidden className="absolute inset-0 bg-gradient-to-r from-[#0f2a5c]/85 via-[#0f2a5c]/40 to-transparent" />
          <motion.div
            aria-hidden
            className="pointer-events-none absolute -inset-y-4 -left-1/2 w-1/3 bg-gradient-to-r from-transparent via-white/25 to-transparent skew-x-12"
            animate={{ left: ["-50%", "150%"] }}
            transition={{ duration: 5, repeat: Infinity, ease: "linear", repeatDelay: 4 }}
          />
          <div className="absolute inset-0 flex items-center">
            <div className="px-8 md:px-14 max-w-2xl text-white">
              <p className="text-[11px] uppercase tracking-[0.3em] text-amber-300 font-bold">{t.chip}</p>
              <h2 className="mt-4 font-display text-4xl md:text-5xl lg:text-6xl font-black tracking-tight leading-[1.02]">
                {t.title_line1}<br />
                <em className="not-italic text-amber-300">{t.title_line2}</em>
              </h2>
              <p className="mt-6 text-base md:text-lg text-sky-100/90 max-w-xl leading-relaxed">{t.subtitle}</p>
              <div className="mt-8 flex flex-wrap items-center gap-3">
                <Link to="/contact" data-testid="truck-wholesale-btn"
                  className="inline-flex items-center gap-2 rounded-full bg-amber-400 text-[#0f2a5c] font-display font-bold px-7 py-3.5 text-sm uppercase tracking-widest hover:bg-white transition-colors">
                  Wholesale Enquiries <ArrowRight size={16} />
                </Link>
                <Link to="/about"
                  className="inline-flex items-center gap-2 rounded-full border-2 border-white/40 text-white font-display font-bold px-7 py-3.5 text-sm uppercase tracking-widest hover:bg-white/10">
                  About Us
                </Link>
              </div>
            </div>
          </div>
        </motion.div>

        <div className="mt-8 grid grid-cols-1 md:grid-cols-2 gap-8" data-testid="truck-supporting-grid">
          {[
            { src: t.image_factory, label: "Our Facility", title: t.factory_title, copy: t.factory_copy, testid: "photo-factory", floatDelay: 0, accent: "bg-amber-400", stat: "USA", statLabel: "Made in" },
            { src: t.image_shelf, label: "On The Shelf", title: t.shelf_title, copy: t.shelf_copy, testid: "photo-shelf", floatDelay: 1.5, accent: "bg-sky-400", stat: "50", statLabel: "States shipped" },
          ].map((card, i) => (
            <motion.div
              key={card.testid}
              initial={{ opacity: 0, y: 60, rotateX: -10 }}
              whileInView={{ opacity: 1, y: 0, rotateX: 0 }}
              viewport={{ once: true, margin: "-60px" }}
              transition={{ duration: 0.9, delay: i * 0.18, ease: [0.22, 1, 0.36, 1] }}
              whileHover={{ y: -12, rotateX: 2, rotateY: i === 0 ? 2 : -2, scale: 1.01 }}
              data-testid={card.testid}
              style={{ transformStyle: "preserve-3d", perspective: 1000 }}
              className="group relative rounded-[28px] overflow-hidden shadow-[0_30px_60px_-30px_rgba(23,58,122,0.45)] border border-slate-100 bg-white"
            >
              {/* Glowing gradient border on hover */}
              <div aria-hidden className="pointer-events-none absolute inset-0 rounded-[28px] opacity-0 group-hover:opacity-100 transition-opacity duration-500 bg-gradient-to-br from-sky-300/40 via-transparent to-amber-300/40" />

              <div className="relative aspect-[4/3] overflow-hidden">
                {/* Slow ken-burns motion */}
                <motion.img
                  src={card.src}
                  alt=""
                  className="absolute inset-0 w-full h-full object-cover"
                  animate={{ scale: [1, 1.08, 1], x: [0, -6, 0], y: [0, -6, 0] }}
                  transition={{ duration: 14, repeat: Infinity, ease: "easeInOut", delay: card.floatDelay }}
                />

                {/* Diagonal shine sweep — loops every 6s */}
                <motion.div
                  aria-hidden
                  className="pointer-events-none absolute -inset-y-8 w-1/3 bg-gradient-to-r from-transparent via-white/45 to-transparent skew-x-12"
                  animate={{ left: ["-40%", "140%"] }}
                  transition={{ duration: 3.5, repeat: Infinity, ease: "easeInOut", repeatDelay: 4, delay: card.floatDelay }}
                />

                {/* Floating sparkles */}
                {[
                  { top: "22%", left: "18%", size: 5, delay: 0 },
                  { top: "55%", left: "78%", size: 4, delay: 1.4 },
                  { top: "72%", left: "32%", size: 3, delay: 2.6 },
                ].map((s, si) => (
                  <motion.span
                    key={si}
                    className="absolute rounded-full bg-white shadow-[0_0_18px_rgba(255,255,255,0.95)]"
                    style={{ top: s.top, left: s.left, width: s.size, height: s.size }}
                    animate={{ y: [0, -18, 0], opacity: [0.2, 1, 0.2], scale: [0.8, 1.4, 0.8] }}
                    transition={{ duration: 3.5 + si, repeat: Infinity, ease: "easeInOut", delay: s.delay + card.floatDelay }}
                  />
                ))}

                {/* Bottom gradient veil */}
                <div className="absolute inset-x-0 bottom-0 h-2/3 bg-gradient-to-t from-[#0f2a5c]/75 via-[#0f2a5c]/20 to-transparent" />

                {/* Floating label chip */}
                <motion.div
                  className="absolute top-4 left-4 flex items-center gap-2 rounded-full bg-white/95 px-3 py-1.5 text-[10px] uppercase tracking-[0.2em] font-bold text-[#173a7a] shadow-lg"
                  animate={{ y: [0, -6, 0], x: [0, 3, 0] }}
                  transition={{ duration: 4, repeat: Infinity, ease: "easeInOut", delay: card.floatDelay + 0.5 }}
                >
                  <motion.span
                    animate={{ scale: [1, 1.6, 1], opacity: [1, 0.5, 1] }}
                    transition={{ duration: 1.4, repeat: Infinity, delay: card.floatDelay }}
                    className={`w-1.5 h-1.5 rounded-full ${card.accent}`}
                  />
                  {card.label}
                </motion.div>

                {/* Animated big stat overlay (bottom-right) */}
                <motion.div
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ delay: 0.6 + i * 0.15, duration: 0.7 }}
                  className="absolute bottom-4 right-4 text-right text-white"
                >
                  <p className="font-display text-4xl md:text-5xl font-black leading-none">
                    <motion.span
                      animate={{ scale: [1, 1.06, 1] }}
                      transition={{ duration: 3, repeat: Infinity, ease: "easeInOut", delay: card.floatDelay }}
                      className="inline-block"
                    >
                      {card.stat}
                    </motion.span>
                  </p>
                  <p className="mt-1 text-[10px] uppercase tracking-[0.25em] font-bold text-amber-300">{card.statLabel}</p>
                </motion.div>
              </div>

              <div className="p-6 md:p-7">
                <motion.p
                  initial={{ opacity: 0, y: 10 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ delay: 0.45 + i * 0.15, duration: 0.6 }}
                  className="font-display text-xl md:text-2xl font-black text-[#0f2a5c]"
                >
                  {card.title}
                </motion.p>
                <motion.p
                  initial={{ opacity: 0, y: 10 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ delay: 0.55 + i * 0.15, duration: 0.6 }}
                  className="mt-2 text-sm text-slate-600 leading-relaxed"
                >
                  {card.copy}
                </motion.p>
                {/* Animated underline that grows on hover */}
                <motion.div
                  className={`mt-4 h-1 rounded-full ${card.accent} origin-left`}
                  initial={{ scaleX: 0.15 }}
                  whileHover={{ scaleX: 1 }}
                  animate={{ scaleX: [0.15, 0.4, 0.15] }}
                  transition={{ duration: 4, repeat: Infinity, ease: "easeInOut", delay: card.floatDelay }}
                  style={{ maxWidth: 120 }}
                />
              </div>
            </motion.div>
          ))}
        </div>

        <div className="mt-10 grid grid-cols-1 md:grid-cols-3 gap-4">
          {[
            { icon: PackageCheck, title: "Consistent Supply", desc: "Reliable inventory, backed by U.S.-based logistics." },
            { icon: Store, title: "Retail Ready", desc: "Attractive packaging that sells itself on the shelf." },
            { icon: Building2, title: "B2B Partnerships", desc: "Serving homes, hotels, healthcare, offices and more." },
          ].map((tc) => (
            <div key={tc.title} className="rounded-2xl border border-slate-100 p-5 bg-white" data-testid={`truck-chip-${tc.title.toLowerCase().replace(/\s/g, "-")}`}>
              <div className="w-10 h-10 rounded-full bg-sky-50 flex items-center justify-center text-[#173a7a]"><tc.icon size={18} /></div>
              <p className="mt-4 font-display font-bold text-slate-900">{tc.title}</p>
              <p className="mt-1 text-sm text-slate-500 leading-relaxed">{tc.desc}</p>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}
